import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageMeta, eur } from "@/lib/format";
import { useT } from "@/i18n";
import { toast } from "sonner";
import AssistantDashboardCard from "@/pages/coach/assistant/DashboardCard";
import {
  CalendarCheck, Wallet, Users, Gem, HeartPulse, Sparkles, Clock, Store,
  BarChart3, FileText, Handshake, ChevronRight, ChevronDown, Copy, Check, UserRound,
  ShieldCheck, Layers, ClipboardList, Megaphone, MessageSquareQuote, FileSignature,
  Rocket, Settings, Crown, Users2, Trophy, LifeBuoy, LayoutDashboard, Rss, MessageCircle,
  Bell, Bot, Gift, Share2, CreditCard, TrendingUp, TrendingDown,
} from "lucide-react";

// Six category tiles mirroring the left-sidebar coach grouping. Labels/subs are
// i18n keys resolved at render time via t().
const GROUPS = [
  { key: "start", labelKey: "ch.g_start", icon: LayoutDashboard, tone: "brand", items: [
    { key: "feed", to: "/app/feed", labelKey: "ch.i_feed", subKey: "ch.s_feed", icon: Rss },
    { key: "messages", to: "/app/messages", labelKey: "ch.i_messages", subKey: "ch.s_messages", icon: MessageCircle },
    { key: "notifications", to: "/app/notifications", labelKey: "ch.i_notifications", subKey: "ch.s_notifications", icon: Bell },
  ] },
  { key: "circles", labelKey: "ch.g_circles", icon: Users, tone: "accent", items: [
    { key: "circles", to: "/app/coach/circles", labelKey: "ch.i_circles", subKey: "ch.s_circles", icon: Sparkles },
    { key: "inner", to: "/app/coach/circles/inner", labelKey: "ch.i_inner", subKey: "ch.s_inner", icon: Gem },
    { key: "mein-kreis", to: "/app/coach/circles/mein-kreis", labelKey: "ch.i_mein", subKey: "ch.s_mein", icon: Users2 },
    { key: "pulse", to: "/app/coach/client-pulse", labelKey: "ch.i_pulse", subKey: "ch.s_pulse", icon: HeartPulse },
  ] },
  { key: "sell", labelKey: "ch.g_sell", icon: Store, tone: "brand", items: [
    { key: "offerings", to: "/app/coach/offerings", labelKey: "nav.offerings", subKey: "ch.s_offerings", icon: Store },
    { key: "bookings", to: "/app/coach/bookings", labelKey: "nav.bookings", subKey: "ch.s_bookings", icon: CalendarCheck },
    { key: "earnings", to: "/app/coach/earnings", labelKey: "nav.earnings", subKey: "ch.s_earnings", icon: Wallet },
    { key: "availability", to: "/app/coach/availability", labelKey: "nav.availability", subKey: "ch.s_availability", icon: Clock },
  ] },
  { key: "grow", labelKey: "ch.g_grow", icon: Sparkles, tone: "accent", items: [
    { key: "assistant", to: "/app/coach/assistant", labelKey: "ch.i_assistant", subKey: "ch.s_assistant", icon: Bot },
    { key: "analytics", to: "/app/coach/analytics", labelKey: "nav.analytics", subKey: "ch.s_analytics", icon: BarChart3 },
    { key: "ads", to: "/app/coach/ads", labelKey: "ch.i_ads", subKey: "ch.s_ads", icon: Megaphone },
    { key: "recommendations", to: "/app/coach/recommendations", labelKey: "ch.i_recommendations", subKey: "ch.s_recommendations", icon: MessageSquareQuote },
    { key: "collab", to: "/app/coach/collab", labelKey: "nav.collab", subKey: "ch.s_collab", icon: Handshake },
    { key: "content", to: "/app/coach/content", labelKey: "ch.i_content", subKey: "ch.s_content", icon: FileText },
    { key: "methods", to: "/app/coach/methods", labelKey: "nav.methods", subKey: "ch.s_methods", icon: Layers },
    { key: "plans", to: "/app/coach/plans", labelKey: "nav.plans", subKey: "ch.s_plans", icon: ClipboardList },
  ] },
  { key: "account", labelKey: "ch.g_account", icon: Settings, tone: "muted", items: [
    { key: "profile", to: "/app/coach/profile", labelKey: "nav.profile", subKey: "ch.s_profile", icon: UserRound },
    { key: "verify", to: "/app/coach/verify", labelKey: "nav.verify", subKey: "ch.s_verify", icon: ShieldCheck },
    { key: "consent-forms", to: "/app/coach/consent-forms", labelKey: "ch.i_consent", subKey: "ch.s_consent", icon: FileSignature },
    { key: "client-wins", to: "/app/coach/client-wins", labelKey: "ch.i_client_wins", subKey: "ch.s_client_wins", icon: Trophy },
    { key: "collectives", to: "/app/coach/collectives", labelKey: "ch.i_collectives", subKey: "ch.s_collectives", icon: Users2 },
    { key: "settings", to: "/app/coach/settings", labelKey: "ch.i_settings", subKey: "ch.s_settings", icon: Settings },
    { key: "billing", to: "/app/billing", labelKey: "ch.i_billing", subKey: "ch.s_billing", icon: CreditCard },
    { key: "upgrade", to: "/app/coach/upgrade", labelKey: "ch.i_upgrade", subKey: "ch.s_upgrade", icon: Crown },
    { key: "support", to: "/app/coach/support", labelKey: "ch.i_support", subKey: "ch.s_support", icon: LifeBuoy },
  ] },
  { key: "affiliate", labelKey: "ch.g_affiliate", icon: Gift, tone: "accent", items: [
    { key: "affiliate-link", to: "/app/coach/referral", labelKey: "ch.i_aff_link", subKey: "ch.s_aff_link", icon: Share2 },
    { key: "affiliate-offerings", to: "/app/coach/offerings", labelKey: "ch.i_aff_offerings", subKey: "ch.s_aff_offerings", icon: Megaphone },
    { key: "affiliate-payouts", to: "/app/coach/earnings", labelKey: "ch.i_aff_payouts", subKey: "ch.s_aff_payouts", icon: Wallet },
  ] },
];

const TONE = {
  brand: "bg-primary/10 text-primary",
  accent: "bg-amber-100 text-amber-700",
  warn: "bg-rose-100 text-rose-700",
  muted: "bg-accent text-accent-foreground",
};

export default function CoachHome() {
  usePageMeta("Coach-Dashboard");
  const t = useT();
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [circleCount, setCircleCount] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [unread, setUnread] = useState(0);
  const [setup, setSetup] = useState(null);
  const [affLink, setAffLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [earn, setEarn] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "coach") return;
    api.get("/coach/bookings/overview").then(({ data }) => setKpis(data?.kpis || {})).catch(() => setKpis({}));
    api.get("/circles").then(({ data }) => setCircleCount((Array.isArray(data) ? data : data?.rows || []).length)).catch(() => setCircleCount(0));
    api.get("/coach/setup-progress").then(({ data }) => setSetup(data)).catch(() => setSetup(null));
    api.get("/me/referral/share-links").then(({ data }) => setAffLink(data?.web_url || "")).catch(() => {});
    api.get("/coach/bookings/list?scope=upcoming").then(({ data }) => setUpcoming((data?.bookings || []).slice(0, 3))).catch(() => setUpcoming([]));
    api.get("/messages/unread-count").then(({ data }) => setUnread(data?.unread_count || 0)).catch(() => {});
    api.get("/coach/earnings/overview?period=month").then(({ data }) => setEarn(data)).catch(() => setEarn(null));
  }, [user]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const copyAff = () => {
    if (!affLink) return;
    try { navigator.clipboard.writeText(affLink); } catch { /* noop */ }
    setCopied(true);
    toast.success(t("ch.aff_copied"));
    setTimeout(() => setCopied(false), 1500);
  };
  const shareAff = async () => {
    if (!affLink) return;
    if (navigator.share) { try { await navigator.share({ title: "My Within", url: affLink }); return; } catch { /* cancelled */ } }
    copyAff();
  };

  return (
    <div className="space-y-8" data-testid="coach-home">
      <div>
        <p className="text-sm text-muted-foreground">{t("ch.welcome")}</p>
        <h1 className="font-display text-3xl text-foreground">{user?.name?.split(" ")[0] || t("ch.default_name")}</h1>
      </div>

      {/* Work queue — what needs attention now */}
      <section data-testid="coach-work-queue" className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <ActionStat to="/app/coach/bookings" icon={CalendarCheck} label={t("ch.wq_upcoming")} value={kpis?.upcoming ?? "…"} testId="coach-wq-upcoming" />
          <ActionStat to="/app/messages" icon={MessageCircle} label={t("ch.wq_unread")} value={unread} badge={unread > 0} testId="coach-wq-unread" />
          <ActionStat to="/app/coach/bookings" icon={Clock} label={t("ch.wq_pending")} value={kpis?.cancellation_pending ?? 0} badge={(kpis?.cancellation_pending ?? 0) > 0} testId="coach-wq-pending" />
        </div>
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="coach-next-sessions">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-base text-foreground">{t("ch.wq_next_sessions")}</h2>
            <Link to="/app/coach/bookings" className="text-sm font-medium text-primary hover:underline">{t("ch.wq_view_all")}</Link>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("ch.wq_no_sessions")}</p>
          ) : (
            <div className="space-y-2">
              {upcoming.map((b) => (
                <div key={b.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><CalendarCheck className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{b.offering?.title || t("ch.wq_session")}</p>
                    <p className="truncate text-xs text-muted-foreground">{b.client?.name || "—"}{b.scheduled_at ? ` · ${new Date(b.scheduled_at).toLocaleString()}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Earnings summary — prominent (§16) */}
      <section className="rounded-2xl border border-primary/25 bg-primary/5 p-5" data-testid="coach-earnings-banner">
        <div className="mb-4 flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary"><Wallet className="h-3.5 w-3.5" /> {t("ch.earn_title")}</p>
          <Link to="/app/coach/earnings" className="text-sm font-medium text-primary hover:underline" data-testid="coach-earnings-link">{t("ch.earn_view")}</Link>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <div className="flex items-center gap-1.5">
              <p className="font-display text-2xl font-semibold text-foreground" data-testid="coach-earn-gross">{earn ? eur(earn.gross_cents ?? 0) : "…"}</p>
              {earn && earn.prev_gross_cents != null && (() => {
                const cur = earn.gross_cents ?? 0;
                const prev = earn.prev_gross_cents ?? 0;
                if (cur === 0 && prev === 0) return null;
                const up = cur >= prev;
                const pct = prev > 0 ? Math.round(((cur - prev) / prev) * 100) : 100;
                return (
                  <span title={t("ch.earn_vs_prev")} data-testid="coach-earn-trend" className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${up ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{pct >= 0 ? `+${pct}` : pct}%
                  </span>
                );
              })()}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("ch.earn_gross")}</p>
          </div>
          <div>
            <p className="font-display text-2xl font-semibold text-foreground" data-testid="coach-earn-pending">{earn ? eur(earn.pending_cents ?? 0) : "…"}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("ch.earn_pending")}</p>
          </div>
          <div>
            <p className="font-display text-2xl font-semibold text-foreground" data-testid="coach-earn-paid">{earn ? eur(earn.paid_out_cents ?? 0) : "…"}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("ch.earn_paid")}</p>
          </div>
          <div>
            <p className="font-display text-2xl font-semibold text-foreground" data-testid="coach-earn-next">{earn?.next_payout_date ? new Date(earn.next_payout_date).toLocaleDateString() : "—"}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("ch.earn_next")}</p>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">{t("ch.earn_period_note")}</p>
      </section>

      {/* AI-Praxis-Assistent — prominent */}
      <AssistantDashboardCard />

      {/* Setup-Fortschritt-Banner */}
      {setup && !setup.complete && (
        <Link to="/app/coach/setup" data-testid="coach-setup-banner" className="group flex items-center gap-4 rounded-2xl border border-primary/30 bg-primary/5 p-5 transition-colors hover:bg-primary/10">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary"><Rocket className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">{t("ch.setup_title", { done: setup.done ?? 0, total: setup.total ?? 4 })}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("ch.setup_sub")}</p>
            <div className="mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-border">
              <div className="h-1.5 rounded-full bg-primary transition-all" style={{ width: `${Math.round(((setup.done ?? 0) / (setup.total || 4)) * 100)}%` }} />
            </div>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </Link>
      )}

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2">
        <StatBox label={t("ch.stat_circles")} value={circleCount == null ? "…" : circleCount} icon={Sparkles} testId="coach-stat-circles" />
        <StatBox label={t("ch.stat_credits")} value={user?.credits ?? 0} icon={Gem} testId="coach-stat-credits" />
      </div>

      {/* Affiliate link */}
      {affLink ? (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-5" data-testid="coach-affiliate-card">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">{t("ch.aff_title")}</p>
            <p className="mt-1 truncate font-mono text-sm font-medium text-foreground" data-testid="coach-affiliate-link">{affLink}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("ch.aff_hint")}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button onClick={copyAff} data-testid="coach-copy-affiliate" className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:text-foreground">
              {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
            </button>
            <button onClick={shareAff} data-testid="coach-share-affiliate" className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:text-foreground">
              <Share2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}

      {/* Six-tile area */}
      <div>
        <h2 className="mb-4 font-display text-xl text-foreground">{t("ch.area_title")}</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {GROUPS.map((g) => {
            const active = openGroup === g.key;
            return (
              <button
                key={g.key}
                data-testid={`coach-group-${g.key}`}
                onClick={() => setOpenGroup(active ? null : g.key)}
                className={`group flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${active ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-accent/40"}`}
              >
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${TONE[g.tone]}`}>
                  <g.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground">{t(g.labelKey)}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t("ch.areas_count", { n: g.items.length })}</p>
                </div>
                <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${active ? "rotate-180" : ""}`} />
              </button>
            );
          })}
        </div>

        {/* Expanded sub-items */}
        {openGroup && (
          <div className="mt-4 rounded-2xl border border-border bg-secondary/30 p-4" data-testid={`coach-group-panel-${openGroup}`}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {GROUPS.find((g) => g.key === openGroup).items.map((it) => (
                <Link key={it.key} to={it.to} data-testid={`coach-tile-${it.key}`} className="group flex items-start gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-accent/40">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                    <it.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground">{t(it.labelKey)}</p>
                    <p className="mt-0.5 text-sm text-muted-foreground">{t(it.subKey)}</p>
                  </div>
                  <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ActionStat({ to, icon: Icon, label, value, badge, testId }) {
  return (
    <Link to={to} data-testid={testId} className="group flex items-center justify-between rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/40 hover:bg-accent/30">
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="mt-1 font-display text-3xl font-semibold text-foreground">{value}</p>
      </div>
      <span className={`flex h-10 w-10 items-center justify-center rounded-full ${badge ? "bg-rose-100 text-rose-600" : "bg-primary/10 text-primary"}`}><Icon className="h-5 w-5" /></span>
    </Link>
  );
}

function StatBox({ label, value, icon: Icon, testId }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{label}</p>
        <Icon className="h-5 w-5 text-sage-light" />
      </div>
      <p className="mt-2 font-display text-3xl font-semibold text-foreground">{value}</p>
    </div>
  );
}
